import { useEffect } from "react"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { Provider, useSelector } from "react-redux"

import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom"

import TomatoRoll from "~/pages/TomatoRoll/TomatoRoll"
import Notification from "~/components/Notification/Notification"
import SignIn from "~/pages/SignIn/SignIn"
import Spaces from "~/pages/Spaces/Spaces"
import Space from "~/pages/Space/Space"
import SharedSpace from "~/pages/SharedSpace/SharedSpace"
import NewSpace from "~/pages/NewSpace/NewSpace"
import NewOrganisation from "~/pages/NewOrganisation/NewOrganisation"
import InviteToOrganisation from "~/pages/InviteToOrganisation/InviteToOrganisation"
import SettingsProfile from "~/pages/SettingsProfile/SettingsProfile"
import SettingsOrganisation from "~/pages/SettingsOrganisation/SettingsOrganisation"
import SettingsUsers from "~/pages/SettingsUsers/SettingsUsers"
import Dashboard from "~/pages/Dashboard/Dashboard"
import SignUp from "~/pages/SignUp/SignUp"
import NewPassword from "~/pages/NewPassword/NewPassword"
import EditPassword from "~/pages/EditPassword/EditPassword"
import EmailVerification from "./pages/EmailVerification/EmailVerification"
import AcceptInvite from "./pages/AcceptInvite/AcceptInvite"
import NotFound from "./pages/NotFound/NotFound"
import {
  fetchUser,
  getUser,
  getUserFetching,
  store,
  useAppDispatch,
} from "~/store"
import ActiveSpacePageProvider from "./providers/ActiveSpacePageProvider"
import SharedSpaceProvider from "./providers/SharedSpaceProvider"
import SpaceChannel from "./channels/SpaceChannel"
import SpacesChannel from "./channels/SpacesChannel"
import GlobalTimerEffects from "./components/GlobalTimerEffects/GlobalTimerEffects"
import { User } from "./types"

function AppWithProvider() {
  const dispatch = useAppDispatch()
  const user = useSelector(getUser)
  const fetchingUser = useSelector(getUserFetching)

  useEffect(() => {
    dispatch(fetchUser())
  }, [])

  if (fetchingUser) return <TomatoRoll />
  if (user && !user.confirmed) return <EmailVerification />
  if (!user) return notLoggedIn()
  return loggedIn(user)
}

function notLoggedIn() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/signup" element={<SignUp />} />
        <Route path="/password/new" element={<NewPassword />} />
        <Route
          path="/password/edit/:resetPasswordToken"
          element={<EditPassword />}
        />
        <Route
          path="/invite/accept/:invitationToken"
          element={<AcceptInvite />}
        />
        <Route
          path="/shared/:shareToken"
          element={
            <SharedSpaceProvider>
              <SharedSpace />
            </SharedSpaceProvider>
          }
        />
        <Route path="/" element={<SignIn />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  )
}
function loggedIn(user: User) {
  return (
    <BrowserRouter>
      <SpacesChannel />
      <SpaceChannel />
      <GlobalTimerEffects />
      <Routes>
        <Route
          path="/"
          element={
            user.space_name ? (
              <Navigate to={`/spaces/${user.space_name}`} replace />
            ) : (
              <Navigate to="/spaces" replace />
            )
          }
        />
        <Route path="/signin" element={<Navigate to="/spaces" replace />} />
        <Route path="/password/*" element={<Navigate to="/spaces" replace />} />
        <Route
          path="/settings"
          element={<Navigate to="/settings/profile" replace />}
        />
        <Route path="/settings/profile" element={<SettingsProfile />} />
        <Route
          path="/settings/organisation"
          element={
            user.organisation_name ? (
              <SettingsOrganisation />
            ) : (
              <Navigate to="/settings/profile" replace />
            )
          }
        />
        <Route
          path="/settings/users"
          element={
            user.organisation_name ? (
              <SettingsUsers />
            ) : (
              <Navigate to="/settings/profile" replace />
            )
          }
        />
        <Route path="/spaces/new" element={<NewSpace />} />
        <Route path="/spaces/:spaceName" element={<Space />} />
        <Route path="/spaces" element={<Spaces />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route
          path="/organisations/new"
          element={
            user.organisation_name ? (
              <Navigate to="/spaces" replace />
            ) : (
              <NewOrganisation />
            )
          }
        />
        <Route
          path="/organisations/invite"
          element={
            user.organisation_name && user.admin ? (
              <InviteToOrganisation />
            ) : (
              <Navigate to="/spaces" replace />
            )
          }
        />
        <Route
          path="/shared/:shareToken"
          element={
            <SharedSpaceProvider>
              <SharedSpace />
            </SharedSpaceProvider>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  )
}

const queryClient = new QueryClient()

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <Notification />
        <ActiveSpacePageProvider>
          <AppWithProvider />
        </ActiveSpacePageProvider>
      </Provider>
    </QueryClientProvider>
  )
}
